import React, { useContext, useMemo, useState } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  Row,
  Col,
  Badge,
} from "react-bootstrap";
import {
  FaMapMarkerAlt,
  FaRupeeSign,
  FaBed,
  FaBath,
  FaRuler,
  FaPhone,
  FaEnvelope,
  FaPlusCircle,
  FaComments,
  FaLocationArrow,
  FaCrown,
} from "react-icons/fa";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { AuthContext } from "../context/AuthContext";

export default function Rooms() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stateQuery, setStateQuery] = useState(user?.state || "");
  const [cityQuery, setCityQuery] = useState(user?.city || "");
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(false);
  const [chatError, setChatError] = useState("");
  const [startingChatId, setStartingChatId] = useState("");
  const [usingNearby, setUsingNearby] = useState(false);

  const userId = user?._id || user?.id || "";

  const sortedRooms = useMemo(() => {
    const roomList = Array.isArray(rooms) ? [...rooms] : [];
    if (roomList.length === 0) {
      return roomList;
    }

    const normalize = (value) =>
      typeof value === "string" ? value.trim().toLowerCase() : "";

    const referenceCity = normalize(user?.city) || normalize(cityQuery);
    const referenceState = normalize(user?.state) || normalize(stateQuery);

    const isPrimeHost = (room) =>
      Boolean(room?.sellerPrime || room?.createdBy?.isPrime);

    const isSameCity = (room) => {
      if (!referenceCity) {
        return false;
      }
      const roomCity = normalize(room?.city);
      if (!roomCity || roomCity !== referenceCity) {
        return false;
      }
      if (!referenceState) {
        return true;
      }
      const roomState = normalize(room?.state);
      return !roomState || roomState === referenceState;
    };

    const isSameState = (room) => {
      if (!referenceState) {
        return false;
      }
      const roomState = normalize(room?.state);
      return roomState === referenceState;
    };

    const getGroupPriority = (room) => {
      const sameCity = isSameCity(room);
      const prime = isPrimeHost(room);
      if (sameCity && prime) return 0;
      if (sameCity && !prime) return 1;
      if (!sameCity && prime) return 2;
      return 3;
    };

    const getStatePriority = (room) => (isSameState(room) ? 0 : 1);

    const getDistance = (room) => {
      const rawDistance =
        room?.distanceKilometers ?? room?.distanceKm ?? room?.distance;
      const numeric = Number(rawDistance);
      return Number.isFinite(numeric) ? numeric : null;
    };

    return roomList.sort((a, b) => {
      const groupDiff = getGroupPriority(a) - getGroupPriority(b);
      if (groupDiff !== 0) {
        return groupDiff;
      }

      const stateDiff = getStatePriority(a) - getStatePriority(b);
      if (stateDiff !== 0) {
        return stateDiff;
      }

      const aDistance = getDistance(a);
      const bDistance = getDistance(b);
      if (aDistance !== bDistance) {
        if (aDistance === null) return 1;
        if (bDistance === null) return -1;
        return aDistance - bDistance;
      }

      const aCreated = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bCreated = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bCreated - aCreated;
    });
  }, [rooms, user?.city, user?.state, cityQuery, stateQuery]);

  const getCurrentPosition = () =>
    new Promise((resolve, reject) => {
      if (!navigator?.geolocation) {
        reject(new Error("Geolocation is not supported in this browser."));
        return;
      }

      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 5 * 60 * 1000,
      });
    });

  const handleSearch = async (event) => {
    event.preventDefault();
    setError("");
    setChatError("");
    setStartingChatId("");
    setRooms([]);
    setUsingNearby(false);

    if (!stateQuery.trim() || !cityQuery.trim()) {
      setError("Please enter both state and city to search for rooms.");
      return;
    }

    setLoading(true);
    try {
      const res = await api.get("/rooms", {
        params: {
          state: stateQuery.trim(),
          city: cityQuery.trim(),
        },
      });
      setRooms(res.data?.rooms || []);
      setSearched(true);
    } catch (err) {
      console.error("Room search error:", err);
      setRooms([]);
      setSearched(true);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to load rooms for the selected location."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleNearbySearch = async () => {
    setError("");
    setChatError("");
    setStartingChatId("");
    setRooms([]);
    setUsingNearby(true);
    setSearched(false);
    setLoading(true);

    try {
      const position = await getCurrentPosition();
      const { latitude, longitude } = position.coords || {};

      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        setError("Unable to determine your location.");
        setRooms([]);
        setSearched(true);
        return;
      }

      const res = await api.get("/rooms/nearby", {
        params: {
          latitude,
          longitude,
          radiusKm: 20,
        },
      });

      setRooms(res.data?.rooms || []);
      setSearched(true);
    } catch (err) {
      console.error("Nearby room search error:", err);
      setRooms([]);
      setSearched(true);

      if (err && typeof err.code === "number") {
        if (err.code === 1) {
          setError(
            "Location permission denied. Please enable it to see nearby rooms."
          );
        } else if (err.code === 2) {
          setError(
            "Your location is currently unavailable. Try again in a moment."
          );
        } else if (err.code === 3) {
          setError("Locating took too long. Please try again.");
        } else {
          setError("Unable to get your location.");
        }
      } else if (err?.response?.data) {
        setError(
          err.response.data.message ||
            err.response.data.error ||
            "Unable to load nearby rooms."
        );
      } else {
        setError(err?.message || "Unable to load nearby rooms.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStartChat = async (room) => {
    if (!room || !room._id) {
      return;
    }
    if (!userId) {
      setChatError("You must be signed in to message the room owner.");
      return;
    }

    const ownerRaw =
      room && room.createdBy && typeof room.createdBy === "object"
        ? room.createdBy._id || room.createdBy.id || ""
        : room?.createdBy || "";
    const ownerId = ownerRaw ? String(ownerRaw) : "";

    if (ownerId && ownerId === String(userId)) {
      setChatError("This is your room listing.");
      return;
    }

    setChatError("");
    setStartingChatId(String(room._id));

    try {
      const res = await api.post("/chats", {
        roomId: room._id,
        buyerId: userId,
      });

      const conversationId = res.data?.conversation?._id;
      if (conversationId) {
        navigate(`/chats?conversationId=${conversationId}`);
      } else {
        setChatError(
          res.data?.message || "Unable to start chat with the room owner."
        );
      }
    } catch (err) {
      console.error("Start room chat error:", err);
      setChatError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Unable to start chat with the room owner."
      );
    } finally {
      setStartingChatId("");
    }
  };

  return (
    <Container className="py-4">
      <Row className="g-4">
        <Col lg={4}>
          <Card className="shadow-sm border-0 rounded-4 h-100">
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <FaMapMarkerAlt className="text-primary me-2" size={20} />
                <h5 className="mb-0">Find rooms near you</h5>
              </div>
              <Form onSubmit={handleSearch}>
                <Form.Group className="mb-3">
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    value={stateQuery}
                    onChange={(event) => setStateQuery(event.target.value)}
                    placeholder="Karnataka"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    value={cityQuery}
                    onChange={(event) => setCityQuery(event.target.value)}
                    placeholder="Bengaluru"
                    required
                  />
                </Form.Group>
                <div className="d-grid gap-2">
                  <Button type="submit" disabled={loading}>
                    {loading ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      "Search"
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline-primary"
                    disabled={loading}
                    onClick={handleNearbySearch}
                    className="d-flex align-items-center justify-content-center gap-2"
                  >
                    {loading && usingNearby ? (
                      <>
                        <Spinner animation="border" size="sm" />
                        <span>Locating...</span>
                      </>
                    ) : (
                      <>
                        <FaLocationArrow /> Show rooms near me
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    href="/rooms/post"
                    className="d-flex align-items-center justify-content-center gap-2"
                  >
                    <FaPlusCircle /> Post a room
                  </Button>
                </div>
              </Form>
              {error && (
                <Alert variant="warning" className="mt-3 mb-0">
                  {error}
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={8}>
          {chatError && (
            <Alert variant="danger" className="mb-3">
              {chatError}
            </Alert>
          )}
          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" />
            </div>
          ) : sortedRooms.length === 0 ? (
            <Card className="shadow-sm border-0 rounded-4 h-100">
              <Card.Body className="text-center text-muted d-flex flex-column justify-content-center">
                <FaBed size={56} className="mb-3 mx-auto" />
                {searched ? (
                  <>
                    <div className="fw-semibold">
                      {usingNearby
                        ? "No rooms found near your location."
                        : "No rooms found for this location."}
                    </div>
                    <div>
                      {usingNearby
                        ? "Try expanding your radius or check back soon."
                        : "Try a different city or ask someone to add a listing."}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="fw-semibold">
                      Search for rooms near you.
                    </div>
                    <div>
                      Enter a state and city or use the nearby option to see
                      available rooms.
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          ) : (
            <Row className="g-4">
              {sortedRooms.map((room, index) => {
                const ownerRaw =
                  room && room.createdBy && typeof room.createdBy === "object"
                    ? room.createdBy._id || room.createdBy.id || ""
                    : room?.createdBy || "";
                const ownerId = ownerRaw ? String(ownerRaw) : "";
                const isOwner =
                  ownerId && userId ? ownerId === String(userId) : false;
                const roomIdString = room._id ? String(room._id) : "";
                const chatLoading =
                  startingChatId && roomIdString
                    ? startingChatId === roomIdString
                    : false;
                const parsedDistance = Number(room?.distanceKilometers);
                const distanceKilometers = Number.isFinite(parsedDistance)
                  ? parsedDistance
                  : null;
                const isPrimeSeller = Boolean(
                  room?.sellerPrime ||
                    (room?.createdBy && room.createdBy.isPrime)
                );

                return (
                  <Col key={room._id || index} md={6}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05, duration: 0.4 }}
                      whileHover={{ scale: 1.02 }}
                    >
                      <Card className="h-100 shadow border-0 rounded-4">
                        {room.image && (
                          <div
                            className="card-img-top"
                            style={{
                              height: 180,
                              backgroundImage: `url(${room.image})`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                              borderTopLeftRadius: "1rem",
                              borderTopRightRadius: "1rem",
                            }}
                          />
                        )}
                        <Card.Body>
                          <div className="d-flex align-items-center justify-content-between mb-3">
                            <h5 className="mb-0 fw-bold">{room.title}</h5>
                            <div className="d-flex align-items-center gap-2">
                              {isPrimeSeller && (
                                <Badge
                                  bg="warning"
                                  text="dark"
                                  className="d-inline-flex align-items-center gap-1"
                                >
                                  <FaCrown /> Prime Host
                                </Badge>
                              )}
                              <Badge bg="primary">{room.city}</Badge>
                              {distanceKilometers !== null && (
                                <Badge
                                  bg="success"
                                  title="Distance from your current location"
                                  className="d-inline-flex align-items-center gap-1"
                                >
                                  <FaLocationArrow /> {distanceKilometers} km
                                </Badge>
                              )}
                            </div>
                          </div>
                          <Card.Subtitle className="mb-2 text-muted">
                            <FaMapMarkerAlt className="text-primary me-1" />
                            {room.city}, {room.state}
                          </Card.Subtitle>
                          <Card.Text className="mb-3">
                            <span className="d-inline-flex align-items-center gap-1 fw-semibold me-3">
                              <FaRupeeSign /> {room.price}
                            </span>
                            <span className="d-inline-flex align-items-center gap-1 me-3">
                              <FaBed /> {room.beds} beds
                            </span>
                            <span className="d-inline-flex align-items-center gap-1">
                              <FaBath /> {room.baths} baths
                            </span>
                          </Card.Text>
                          {room.area != null && (
                            <div className="text-secondary mb-2">
                              <FaRuler className="me-2" /> {room.area} sq. ft.
                            </div>
                          )}
                          {room.description && (
                            <div
                              className="text-secondary"
                              style={{ minHeight: 48 }}
                            >
                              {room.description}
                            </div>
                          )}
                        </Card.Body>
                        <Card.Footer className="bg-light border-0">
                          <div className="small text-muted">
                            {isOwner
                              ? "Your contact details"
                              : "Connect with the owner"}
                          </div>
                          {isOwner ? (
                            <div className="small d-flex flex-column gap-1 mt-1">
                              {room.contactPhone ? (
                                <span className="d-inline-flex align-items-center gap-2">
                                  <FaPhone className="text-primary" />{" "}
                                  {room.contactPhone}
                                </span>
                              ) : (
                                <span className="text-muted">
                                  Phone not provided
                                </span>
                              )}
                              {room.contactEmail ? (
                                <span className="d-inline-flex align-items-center gap-2">
                                  <FaEnvelope className="text-primary" />{" "}
                                  {room.contactEmail}
                                </span>
                              ) : (
                                <span className="text-muted">
                                  Email not provided
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="small text-muted mt-1">
                              Share your interest through chat to coordinate
                              next steps.
                            </div>
                          )}
                          {!isOwner && room._id && (
                            <div className="mt-3 d-flex justify-content-between align-items-center gap-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="d-inline-flex align-items-center gap-2"
                                onClick={() => handleStartChat(room)}
                                disabled={chatLoading}
                              >
                                {chatLoading ? (
                                  <>
                                    <Spinner animation="border" size="sm" />
                                    <span>Opening...</span>
                                  </>
                                ) : (
                                  <>
                                    <FaComments /> Message owner
                                  </>
                                )}
                              </Button>
                            </div>
                          )}
                          {isOwner && (
                            <div className="mt-3 small text-muted">
                              You posted this room.
                            </div>
                          )}
                        </Card.Footer>
                      </Card>
                    </motion.div>
                  </Col>
                );
              })}
            </Row>
          )}
        </Col>
      </Row>
    </Container>
  );
}
